import { useState, useEffect, useCallback } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import { getTokenPayload } from "../../components/ProtectedRoute";
import adminFetch from "../../utils/adminFetch";

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

const ROLE_CONFIG = {
  "super-admin": { label: "Super Admin", bg: "#FEF3C7", color: "#92400E" },
  "admin":        { label: "Admin",       bg: "#DBEAFE", color: "#1D4ED8" },
};

function RoleBadge({ role }) {
  const c = ROLE_CONFIG[role] || ROLE_CONFIG.admin;
  return (
    <span style={{ background: c.bg, color: c.color, padding: "2px 10px", borderRadius: "20px",
      fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
      fontFamily: "Arial, sans-serif" }}>
      {c.label}
    </span>
  );
}

// ── User Form Modal ───────────────────────────────────────────────────
function UserModal({ user, currentAdmin, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name:     user?.name     || "",
    username: user?.username || "",
    email:    user?.email    || "",
    role:     user?.role     || "admin",
    password: "",
    active:   user?.active !== undefined ? user.active : true,
  });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [showPw,  setShowPw]  = useState(false);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = isEdit
        ? { name: form.name, email: form.email, role: form.role, active: form.active,
            ...(form.password ? { password: form.password } : {}) }
        : { name: form.name, username: form.username, email: form.email,
            role: form.role, password: form.password };

      const url    = isEdit
        ? `${process.env.REACT_APP_API_URL}/api/admin/users/${user._id}`
        : `${process.env.REACT_APP_API_URL}/api/admin/users`;
      const method = isEdit ? "PUT" : "POST";

      const res  = await adminFetch(url, { method, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save.");
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72",
    background: "#fff", border: "1px solid rgba(1,46,114,0.15)", borderRadius: "10px",
    padding: "10px 14px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = {
    fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.2em",
    textTransform: "uppercase", color: "rgba(1,46,114,0.5)", marginBottom: "6px", display: "block",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(1,4,10,0.55)", backdropFilter: "blur(5px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ border: "1px solid rgba(0,45,181,0.12)" }}>

        {/* Header */}
        <div className="px-7 py-5 flex items-center gap-4"
          style={{ background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
          <div className="w-10 h-10 rounded-full bg-white bg-opacity-10 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div>
            <p className="font-cinzel uppercase text-white opacity-60 tracking-widest" style={{ fontSize: "8px", letterSpacing: "0.28em" }}>
              {isEdit ? "Edit Account" : "New Account"}
            </p>
            <h2 className="font-cinzel font-bold text-white uppercase" style={{ fontSize: "14px", letterSpacing: "0.06em" }}>
              {isEdit ? `Edit — ${user.username}` : "Add Admin User"}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-4">

          {/* Name + Username */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Display Name *</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Jane Smith" required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Username *</label>
              <input value={form.username} onChange={(e) => set("username", e.target.value)}
                placeholder="jane.smith" required={!isEdit} disabled={isEdit}
                style={{ ...inputStyle, opacity: isEdit ? 0.5 : 1, cursor: isEdit ? "not-allowed" : "text" }} />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="jane@example.com" style={inputStyle} />
          </div>

          {/* Role + Active */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={form.role} onChange={(e) => set("role", e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="admin">Admin</option>
                <option value="super-admin">Super Admin</option>
              </select>
            </div>
            {isEdit && (
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.active ? "active" : "inactive"}
                  onChange={(e) => set("active", e.target.value === "active")}
                  style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          {/* Password */}
          <div>
            <label style={labelStyle}>{isEdit ? "New Password (leave blank to keep)" : "Password *"}</label>
            <div style={{ position: "relative" }}>
              <input type={showPw ? "text" : "password"} value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder={isEdit ? "Enter new password to change…" : "Min 8 characters"}
                required={!isEdit}
                style={{ ...inputStyle, paddingRight: "40px" }} />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", color: "rgba(1,46,114,0.4)", padding: 0 }}>
                {showPw
                  ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
              <p className="font-constantia text-red-600" style={{ fontSize: "13px" }}>{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={saving}
              className="flex-1 font-cinzel uppercase border border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 py-3 rounded-xl transition-all duration-200"
              style={{ fontSize: "9px", letterSpacing: "0.15em" }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-[2] font-cinzel uppercase text-white py-3 rounded-xl transition-all duration-200 disabled:opacity-60"
              style={{ fontSize: "9px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete confirmation ───────────────────────────────────────────────
function DeleteModal({ user, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(1,4,10,0.5)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ border: "1px solid rgba(0,45,181,0.12)" }}>
        <div className="px-7 py-5" style={{ background: "linear-gradient(135deg,#7f1d1d,#dc2626)" }}>
          <p className="font-cinzel uppercase text-white opacity-60 tracking-widest mb-1" style={{ fontSize: "8px", letterSpacing: "0.28em" }}>Confirm Delete</p>
          <h2 className="font-cinzel font-bold text-white uppercase" style={{ fontSize: "14px" }}>Remove Admin Account</h2>
        </div>
        <div className="px-7 py-6">
          <p className="font-constantia text-gray-600 leading-relaxed mb-1" style={{ fontSize: "14px" }}>
            Permanently delete <strong style={{ color: "#012E72" }}>{user.username}</strong>?
          </p>
          <p className="font-constantia text-gray-400 mb-6" style={{ fontSize: "12px" }}>
            This cannot be undone. The account will be removed immediately.
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} disabled={deleting}
              className="flex-1 font-cinzel uppercase border border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 py-3 rounded-xl transition-all"
              style={{ fontSize: "9px", letterSpacing: "0.15em" }}>
              Cancel
            </button>
            <button onClick={onConfirm} disabled={deleting}
              className="flex-1 font-cinzel uppercase text-white py-3 rounded-xl transition-all disabled:opacity-60"
              style={{ fontSize: "9px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#7f1d1d,#dc2626)" }}>
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Change Password Section ───────────────────────────────────────────
function ChangePasswordSection({ currentAdmin }) {
  const isEnvAdmin = currentAdmin?.userId === "__super__";

  const [form,    setForm]    = useState({ current: "", next: "", confirm: "" });
  const [showPw,  setShowPw]  = useState({ current: false, next: false, confirm: false });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null); // { type: 'ok'|'err', text }

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleShow = (k) => setShowPw((p) => ({ ...p, [k]: !p[k] }));

  const inputStyle = {
    width: "100%", fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72",
    background: "#fff", border: "1px solid rgba(1,46,114,0.15)", borderRadius: "10px",
    padding: "10px 14px", paddingRight: "42px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = {
    fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.2em",
    textTransform: "uppercase", color: "rgba(1,46,114,0.5)", marginBottom: "6px", display: "block",
  };

  const eyeBtn = (key) => (
    <button type="button" onClick={() => toggleShow(key)}
      style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
        background: "none", border: "none", cursor: "pointer", color: "rgba(1,46,114,0.4)", padding: 0 }}>
      {showPw[key]
        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      }
    </button>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (form.next !== form.confirm) {
      setMsg({ type: "err", text: "New passwords do not match." });
      return;
    }
    if (form.next.length < 8) {
      setMsg({ type: "err", text: "New password must be at least 8 characters." });
      return;
    }
    setSaving(true);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/me/password`, {
        method: "PUT",
        body: JSON.stringify({ currentPassword: form.current, newPassword: form.next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update password.");
      setMsg({ type: "ok", text: "Password updated successfully." });
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setMsg({ type: "err", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden mb-6" style={{ background: "#FFFFFF", border: "1px solid rgba(1,46,114,0.08)" }}>
      <div className="px-6 py-4" style={{ borderBottom: "1px solid #f1f5f9" }}>
        <h2 className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "14px", letterSpacing: "0.06em" }}>
          Change Password
        </h2>
        <p className="font-constantia mt-0.5" style={{ fontSize: "12px", color: "#94a3b8" }}>
          {isEnvAdmin
            ? "The environment super admin password must be updated in the server .env file."
            : "Update your login password. You'll need your current password to confirm."}
        </p>
      </div>

      {isEnvAdmin ? (
        <div className="px-6 py-5 flex items-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <p className="font-constantia" style={{ fontSize: "13px", color: "#94a3b8" }}>
            Password is managed via the <code style={{ fontFamily: "monospace", fontSize: "12px", background: "#f1f5f9", padding: "1px 5px", borderRadius: "4px" }}>.env</code> file on the server.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4">
          <div>
            <label style={labelStyle}>Current Password *</label>
            <div style={{ position: "relative" }}>
              <input type={showPw.current ? "text" : "password"} value={form.current}
                onChange={(e) => set("current", e.target.value)}
                placeholder="Your current password" required style={inputStyle} />
              {eyeBtn("current")}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>New Password *</label>
              <div style={{ position: "relative" }}>
                <input type={showPw.next ? "text" : "password"} value={form.next}
                  onChange={(e) => set("next", e.target.value)}
                  placeholder="Min 8 characters" required style={inputStyle} />
                {eyeBtn("next")}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Confirm New Password *</label>
              <div style={{ position: "relative" }}>
                <input type={showPw.confirm ? "text" : "password"} value={form.confirm}
                  onChange={(e) => set("confirm", e.target.value)}
                  placeholder="Repeat new password" required style={inputStyle} />
                {eyeBtn("confirm")}
              </div>
            </div>
          </div>

          {msg && (
            <div className="px-4 py-3 rounded-xl"
              style={{ background: msg.type === "ok" ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${msg.type === "ok" ? "#BBF7D0" : "#FECACA"}` }}>
              <p className="font-constantia" style={{ fontSize: "13px", color: msg.type === "ok" ? "#15803d" : "#dc2626" }}>{msg.text}</p>
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={saving}
              className="font-cinzel uppercase text-white px-6 py-3 rounded-xl transition-all duration-200 disabled:opacity-60"
              style={{ fontSize: "9px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
              {saving ? "Updating…" : "Update Password"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function AdminSettings() {
  usePageTitle("Admin Settings");

  const currentAdmin = getTokenPayload();
  const isSuperAdmin = currentAdmin?.role === "super-admin";

  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [modal,     setModal]     = useState(null); // null | { mode: 'add' } | { mode: 'edit', user } | { mode: 'delete', user }
  const [deleting,  setDeleting]  = useState(false);

  const load = useCallback(async () => {
    if (!isSuperAdmin) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/users`);
      const data = await res.json();
      if (data.success) setUsers(data.users);
    } catch {
      setError("Failed to load admin users.");
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!modal?.user) return;
    setDeleting(true);
    try {
      await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${modal.user._id}`, { method: "DELETE" });
      setModal(null);
      load();
    } catch {
      setError("Failed to delete user.");
    } finally {
      setDeleting(false);
    }
  };

  function timeAgo(str) {
    if (!str) return "Never";
    const diff = Date.now() - new Date(str).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7)   return `${days}d ago`;
    return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  const cell = { padding: "14px 16px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" };

  return (
    <div style={{ maxWidth: "860px" }}>

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "22px", letterSpacing: "0.06em" }}>
            Settings
          </h1>
          <p className="font-constantia mt-1" style={{ fontSize: "14px", color: "#94a3b8" }}>
            Manage admin accounts and system access.
          </p>
        </div>
      </div>

      {/* ── Logged-in account card ── */}
      <div className="rounded-2xl mb-6 p-5 flex items-center gap-4"
        style={{ background: "#FFFFFF", border: "1px solid rgba(1,46,114,0.08)" }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "15px", letterSpacing: "0.06em" }}>
            {currentAdmin?.name || currentAdmin?.username || "Administrator"}
          </p>
          <p className="font-constantia" style={{ fontSize: "13px", color: "#94a3b8" }}>
            @{currentAdmin?.username}
          </p>
        </div>
        <RoleBadge role={currentAdmin?.role} />
      </div>

      {/* ── Change Password ── */}
      <ChangePasswordSection currentAdmin={currentAdmin} />

      {/* ── Admin users section (super admin only) ── */}
      {!isSuperAdmin ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: "#FFFFFF", border: "1px solid rgba(1,46,114,0.08)" }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <p className="font-cinzel uppercase" style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#cbd5e1" }}>
            User management requires super admin access
          </p>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid rgba(1,46,114,0.08)" }}>

          {/* Section header */}
          <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #f1f5f9" }}>
            <div>
              <h2 className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "14px", letterSpacing: "0.06em" }}>
                Admin Accounts
              </h2>
              <p className="font-constantia mt-0.5" style={{ fontSize: "12px", color: "#94a3b8" }}>
                {users.length} account{users.length !== 1 ? "s" : ""} (excluding super admin env account)
              </p>
            </div>
            <button
              onClick={() => setModal({ mode: "add" })}
              className="flex items-center gap-2 font-cinzel uppercase py-2.5 px-4 rounded-xl transition-all duration-200"
              style={{ fontSize: "10px", letterSpacing: "0.15em",
                background: "linear-gradient(135deg,#012E72,#002DB5)",
                color: "#fff", boxShadow: "0 4px 14px rgba(0,45,181,0.22)" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add User
            </button>
          </div>

          {error && (
            <div className="mx-6 mt-4 px-4 py-3 rounded-xl" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
              <p className="font-constantia text-red-600" style={{ fontSize: "13px" }}>{error}</p>
            </div>
          )}

          {/* Table header */}
          <div className="grid px-5 py-3" style={{ gridTemplateColumns: "1fr 130px 100px 110px 90px", borderBottom: "2px solid #f1f5f9" }}>
            {["User", "Role", "Status", "Last Login", ""].map((h) => (
              <span key={h} className="font-cinzel uppercase" style={{ fontSize: "9px", letterSpacing: "0.2em", color: "#94a3b8" }}>{h}</span>
            ))}
          </div>

          {loading ? (
            <Spinner />
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <p className="font-cinzel uppercase" style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#cbd5e1" }}>
                No admin accounts yet — add one above
              </p>
            </div>
          ) : (
            users.map((u, i) => (
              <div key={u._id}
                className="grid items-center px-5 transition-colors duration-100"
                style={{ gridTemplateColumns: "1fr 130px 100px 110px 90px",
                  borderBottom: i < users.length - 1 ? "1px solid #f8fafc" : "none" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f7f9ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>

                {/* Name + username */}
                <div style={cell}>
                  <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "12px", letterSpacing: "0.05em" }}>
                    {u.name}
                  </p>
                  <p className="font-constantia" style={{ fontSize: "11px", color: "#94a3b8" }}>
                    @{u.username}{u.email ? ` · ${u.email}` : ""}
                  </p>
                </div>

                {/* Role */}
                <div style={cell}><RoleBadge role={u.role} /></div>

                {/* Active */}
                <div style={cell}>
                  <span style={{
                    background: u.active ? "#D1FAE5" : "#F3F4F6",
                    color: u.active ? "#065F46" : "#6B7280",
                    padding: "2px 8px", borderRadius: "20px", fontSize: "9px",
                    letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
                    fontFamily: "Arial, sans-serif",
                  }}>
                    {u.active ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Last login */}
                <div style={cell}>
                  <p className="font-constantia" style={{ fontSize: "12px", color: "#94a3b8" }}>
                    {timeAgo(u.lastLogin)}
                  </p>
                </div>

                {/* Actions */}
                <div style={{ ...cell, borderBottom: "none" }}>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setModal({ mode: "edit", user: u })}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150"
                      title="Edit user"
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#EEF2F8"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      style={{ color: "#94a3b8" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => setModal({ mode: "delete", user: u })}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150"
                      title="Delete user"
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#dc2626"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; }}
                      style={{ color: "#94a3b8" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modals */}
      {modal?.mode === "add" && (
        <UserModal currentAdmin={currentAdmin} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.mode === "edit" && (
        <UserModal user={modal.user} currentAdmin={currentAdmin} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.mode === "delete" && (
        <DeleteModal user={modal.user} onClose={() => setModal(null)} onConfirm={handleDelete} deleting={deleting} />
      )}
    </div>
  );
}
